import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';

interface ArtworkImage {
  url?: string | null;
  original_name?: string | null;
  distributor?: { name?: string | null } | string | null;
}

const INDEX_RE = /^\d+(?:-\d+)?$/;

const distributorName = (d: ArtworkImage['distributor']): string =>
  typeof d === 'string' ? d : (d?.name ?? '');
import {
  TokenField,
  parseToken,
  resolvePath,
  modifiersFor,
  isJoinModifier,
  JOIN_MODIFIERS,
  JOIN_LABELS,
  MODIFIER_LABELS,
  buildTokenInner,
} from './htmlTokenSchema';

interface TokenConfigDialogProps {
  open: boolean;
  field: TokenField;
  fields: TokenField[];
  currentInner: string;
  onApply: (newInner: string) => void;
  onClose: () => void;
  /**
   * The editor restores its own caret when this dialog closes, so MUI must not
   * bounce focus back to the button that opened the dialog.
   */
  disableRestoreFocus?: boolean;
  /**
   * The campaign's artwork files; when present for the artwork field they are
   * offered as a thumbnail picker instead of requiring typed indices.
   */
  artwork?: ArtworkImage[] | null;
}

const TokenConfigDialog = ({
  open,
  field,
  fields,
  currentInner,
  onApply,
  onClose,
  disableRestoreFocus = false,
  artwork = null,
}: TokenConfigDialogProps) => {
  const isCollection = field.type === 'collection';
  const mods = modifiersFor(field);
  const hasImage = mods.includes('images');
  const itemFields = field.itemFields || [];

  const [scalarMod, setScalarMod] = useState<string>('');
  const [mode, setMode] = useState<'list' | 'table' | 'image'>('list');
  const [listItemField, setListItemField] = useState<string>('');
  const [listJoin, setListJoin] = useState<string>('comma');
  const [tableColumns, setTableColumns] = useState<string[]>([]);
  const [imageSel, setImageSel] = useState<'first' | 'all' | 'custom'>('all');
  const [imageArgs, setImageArgs] = useState<string>('');

  const showPicker = field.path === 'artwork' && (artwork ?? []).length > 0;

  // 1-based indices (into the full artwork list) that the current args string
  // selects - mirrors selectArtworkItems in tokenRenderer: an optional name
  // filter first, then the index parts applied to the filtered list.
  const selectedIndices = useMemo(() => {
    const set = new Set<number>();
    if (!showPicker || !artwork) return set;
    const parts = imageArgs.split(',').map(s => s.trim()).filter(s => s !== '');
    const namePart = parts.find(p => !INDEX_RE.test(p));
    let list = artwork;
    if (namePart !== undefined) {
      const lower = namePart.toLowerCase();
      const matchesDistributor = artwork.some(f => distributorName(f.distributor).toLowerCase() === lower);
      list = artwork.filter(f => matchesDistributor
        ? distributorName(f.distributor).toLowerCase() === lower
        : (f.original_name ?? '').toLowerCase() === lower);
    }
    let any = false;
    for (const part of parts) {
      const m = /^(\d+)(?:-(\d+))?$/.exec(part);
      if (m !== null) {
        any = true;
        const lo = Number(m[1]);
        const hi = m[2] !== undefined ? Number(m[2]) : lo;
        for (let i = lo; i <= hi; i++) {
          if (i >= 1 && i <= list.length) set.add(list.indexOf(list[i - 1]) + 1);
        }
      }
    }
    if (!any) {
      list.forEach((_, i) => set.add(i + 1));
    }
    return set;
  }, [showPicker, artwork, imageArgs]);

  const toggleImage = (idx: number) => {
    const next = new Set(selectedIndices);
    if (next.has(idx)) {
      next.delete(idx);
    } else {
      next.add(idx);
    }
    setImageArgs([...next].sort((a, b) => a - b).join(','));
  };

  useEffect(() => {
    if (!open) return;
    const { path, modifier, args } = parseToken(currentInner);
    if (isCollection) {
      if (modifier === 'table') {
        setMode('table');
        setTableColumns(args || []);
      } else if (modifier === 'image' || modifier === 'images') {
        setMode('image');
        const a = (args || []).join(',');
        if (modifier === 'image') {
          setImageSel('first');
          setImageArgs('');
        } else if (a === '') {
          setImageSel('all');
          setImageArgs('');
        } else {
          setImageSel('custom');
          setImageArgs(a);
        }
      } else {
        setMode('list');
        const resolved = resolvePath(fields, path);
        setListItemField(resolved?.itemPath || itemFields[0]?.path || '');
        setListJoin(isJoinModifier(modifier || '') ? (modifier as string) : 'comma');
      }
    } else {
      setScalarMod(modifier || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentInner]);

  const handleApply = () => {
    let inner: string;
    if (isCollection) {
      if (mode === 'table') {
        inner = buildTokenInner(field.path, 'table', tableColumns);
      } else if (mode === 'image') {
        if (imageSel === 'first') {
          inner = buildTokenInner(field.path, 'image');
        } else if (imageSel === 'all') {
          inner = buildTokenInner(field.path, 'images');
        } else {
          const parts = imageArgs.split(',').map((s) => s.trim()).filter(Boolean);
          inner = parts.length ? buildTokenInner(field.path, 'images', parts) : buildTokenInner(field.path, 'images');
        }
      } else {
        inner = buildTokenInner(`${field.path}.${listItemField}`, listJoin);
      }
    } else {
      inner = buildTokenInner(field.path, scalarMod || undefined);
    }
    onApply(inner);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth disableRestoreFocus={disableRestoreFocus}>
      <DialogTitle>Configure {field.label}</DialogTitle>
      <DialogContent dividers>
        {isCollection ? (
          <>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'list' | 'table' | 'image')} sx={{ mb: 2 }}>
              {hasImage && (
                <FormControlLabel value="image" control={<Radio size="small" />} label="Images (campaign artwork)" />
              )}
              <FormControlLabel value="list" control={<Radio size="small" />} label="List (one value per panel)" />
              <FormControlLabel value="table" control={<Radio size="small" />} label="Table (one row per panel)" />
            </RadioGroup>

            {mode === 'image' ? (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Selection
                </Typography>
                <RadioGroup value={imageSel} onChange={(e) => setImageSel(e.target.value as 'first' | 'all' | 'custom')} sx={{ mb: 1 }}>
                  <FormControlLabel value="first" control={<Radio size="small" />} label="First image" />
                  <FormControlLabel value="all" control={<Radio size="small" />} label="All images" />
                  <FormControlLabel value="custom" control={<Radio size="small" />} label={showPicker ? 'Select images' : 'Selection (indices / distributor / file name)'} />
                </RadioGroup>
                {imageSel === 'custom' && showPicker && artwork && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Pick from the campaign artwork
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, maxHeight: 220, overflowY: 'auto' }}>
                      {artwork.map((f, i) => {
                        const idx = i + 1;
                        const checked = selectedIndices.has(idx);
                        return (
                          <FormControlLabel
                            key={idx}
                            control={<Checkbox size="small" checked={checked} onChange={() => toggleImage(idx)} />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, maxWidth: 170 }}>
                                {f.url ? (
                                  <img
                                    src={f.url}
                                    alt={f.original_name ?? ''}
                                    style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 2, border: '1px solid #e2e8f0', flexShrink: 0 }}
                                  />
                                ) : (
                                  <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2, border: '1px solid #e2e8f0', flexShrink: 0 }}>
                                    <ImageIcon fontSize="small" color="disabled" />
                                  </Box>
                                )}
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" noWrap>{f.original_name ?? `Image ${idx}`}</Typography>
                                  <Typography variant="caption" color="text.secondary">#{idx}</Typography>
                                </Box>
                              </Box>
                            }
                          />
                        );
                      })}
                    </Box>
                  </>
                )}
                {imageSel === 'custom' && (
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={imageArgs}
                    onChange={(e) => setImageArgs(e.target.value)}
                    placeholder="e.g. 1-3, metro"
                    label={showPicker ? 'Or type manually (index / distributor / file name)' : undefined}
                    helperText={showPicker ? undefined : 'Index range (1-3), list (1,3), distributor name, file name, or a range with a name (1-2,metro)'}
                  />
                )}
              </>
            ) : mode === 'list' ? (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Field
                </Typography>
                <RadioGroup value={listItemField} onChange={(e) => setListItemField(e.target.value)} sx={{ mb: 2 }}>
                  {itemFields.map((f) => (
                    <FormControlLabel key={f.path} value={f.path} control={<Radio size="small" />} label={f.label} />
                  ))}
                </RadioGroup>

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Separator
                </Typography>
                <RadioGroup value={listJoin} onChange={(e) => setListJoin(e.target.value)}>
                  {JOIN_MODIFIERS.map((j) => (
                    <FormControlLabel key={j} value={j} control={<Radio size="small" />} label={JOIN_LABELS[j]} />
                  ))}
                </RadioGroup>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Columns
                </Typography>
                <Box>
                  {itemFields.map((f) => (
                    <FormControlLabel
                      key={f.path}
                      control={
                        <Checkbox
                          size="small"
                          checked={tableColumns.includes(f.path)}
                          onChange={(e) =>
                            setTableColumns((prev) =>
                              e.target.checked ? [...prev, f.path] : prev.filter((c) => c !== f.path)
                            )
                          }
                        />
                      }
                      label={f.label}
                    />
                  ))}
                </Box>
              </>
            )}
          </>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Format
            </Typography>
            <RadioGroup value={scalarMod} onChange={(e) => setScalarMod(e.target.value)}>
              <FormControlLabel value="" control={<Radio size="small" />} label="Default" />
              {mods.map((m) => (
                <FormControlLabel key={m} value={m} control={<Radio size="small" />} label={MODIFIER_LABELS[m] ?? m} />
              ))}
            </RadioGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokenConfigDialog;
